<script setup lang="ts">
/**
 * WHERE EACH PAYMENT FORM IS BOOKED in the accountant's Pohoda: a Pokladna and,
 * if they use one, a předkontace — their abbreviations, copied exactly.
 *
 * Nothing is guessed: a form left blank is not booked anywhere, and the export
 * names it rather than putting its money into somebody's idea of a till.
 * Owner and admin (`venue.configure`) edit; others read.
 */
import { computed, onMounted, reactive, ref } from "vue";
import type { ConsoleApi, PohodaSettings } from "../api";

const props = defineProps<{
  api: Pick<ConsoleApi, "venueSettings" | "savePohoda">;
  me: { roleId: string } | null;
  t: (key: string) => string;
}>();

/** The forms the till takes; any other that was saved is shown too. */
const TAKEN = ["CASH", "CARD", "MEAL_VOUCHER"];
/** `typ:idsType` in Stormware's type.xsd. */
const MAX = 19;

const forms = ref<string[]>(TAKEN);
const rows = reactive<Record<string, { cashAccount: string; accounting: string }>>({});
const status = ref<"loading" | "ready" | "failed">("loading");
const busy = ref(false);
const notice = ref<{ tone: "ok" | "bad"; text: string } | null>(null);

const canConfigure = computed(() => props.me === null || ["owner", "admin"].includes(props.me.roleId));
const tooLong = computed(() => Object.values(rows).some((r) => r.cashAccount.trim().length > MAX || r.accounting.trim().length > MAX));

const load = async (): Promise<void> => {
  try {
    const saved = (await props.api.venueSettings()).bookkeeping?.pohoda?.tenders ?? {};
    forms.value = [...TAKEN, ...Object.keys(saved).filter((k) => !TAKEN.includes(k))];
    for (const form of forms.value) {
      rows[form] = { cashAccount: saved[form]?.cashAccount ?? "", accounting: saved[form]?.accounting ?? "" };
    }
    status.value = "ready";
  } catch {
    status.value = "failed";
  }
};
onMounted(load);

const save = async (): Promise<void> => {
  if (tooLong.value || busy.value) return;
  const tenders: Record<string, { cashAccount: string; accounting: string | null }> = {};
  for (const form of forms.value) {
    const row = rows[form]!;
    if (!row.cashAccount.trim()) continue;
    tenders[form] = { cashAccount: row.cashAccount.trim(), accounting: row.accounting.trim() || null };
  }
  busy.value = true;
  notice.value = null;
  try {
    await props.api.savePohoda({ tenders } satisfies PohodaSettings);
    notice.value = { tone: "ok", text: props.t("pohoda.saved") };
  } catch (e) {
    notice.value = { tone: "bad", text: e instanceof Error && e.message ? e.message : props.t("common.failed") };
  } finally {
    busy.value = false;
  }
};
</script>

<template>
  <form class="gf-panel pohodaSet" @submit.prevent="save">
    <h2 class="gf-panel__title">{{ t("pohoda.settings") }}</h2>
    <p class="gf-muted pohodaSet__lead">{{ t("pohoda.settingsLead") }}</p>
    <p v-if="!canConfigure" class="pohodaSet__readOnly" role="note">{{ t("venue.ownerOnly") }}</p>

    <p v-if="status === 'loading'" class="gf-muted" role="status">{{ t("common.loading") }}</p>
    <p v-else-if="status === 'failed'" class="pohodaSet__notice pohodaSet__notice--bad" role="alert">{{ t("common.failed") }}</p>

    <table v-else class="gf-table pohodaSet__table">
      <thead>
        <tr>
          <th scope="col">{{ t("checks.tenders") }}</th>
          <th scope="col">{{ t("pohoda.cashAccount") }}</th>
          <th scope="col">{{ t("pohoda.accounting") }} <span class="gf-muted">({{ t("pohoda.optional") }})</span></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="form in forms" :key="form">
          <th scope="row">{{ t(`tender.${form}`) }}</th>
          <td>
            <input
              v-model="rows[form]!.cashAccount"
              class="gf-input gf-mono"
              :maxlength="MAX + 5"
              :disabled="!canConfigure"
              :data-field="`cashAccount-${form}`"
              :aria-invalid="rows[form]!.cashAccount.trim().length > MAX"
            />
          </td>
          <td>
            <input
              v-model="rows[form]!.accounting"
              class="gf-input gf-mono"
              :maxlength="MAX + 5"
              :disabled="!canConfigure"
              :data-field="`accounting-${form}`"
              :aria-invalid="rows[form]!.accounting.trim().length > MAX"
            />
          </td>
        </tr>
      </tbody>
    </table>

    <p v-if="tooLong" class="pohodaSet__notice pohodaSet__notice--bad" role="alert">{{ t("pohoda.tooLong") }}</p>
    <p v-else-if="notice" class="pohodaSet__notice" :class="`pohodaSet__notice--${notice.tone}`" role="status">{{ notice.text }}</p>

    <div v-if="canConfigure && status === 'ready'" class="pohodaSet__actions">
      <button type="submit" class="gf-button" data-testid="save-pohoda" :disabled="busy || tooLong">{{ t("menu.save") }}</button>
    </div>
  </form>
</template>

<style scoped>
/* Read-only for a manager: a field nobody may change must not look like one. */
.pohodaSet .gf-input:disabled {
  color: var(--gf-color-text-muted);
  background: var(--gf-color-surface-sunken);
  border-color: var(--gf-color-border-subtle);
  cursor: not-allowed;
}

.pohodaSet__readOnly {
  margin: 0;
  font-size: var(--gf-text-sm);
  color: var(--gf-color-text-muted);
}

.pohodaSet {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-3);
}

.pohodaSet__lead {
  margin: 0;
  font-size: var(--gf-text-sm);
  max-width: 48rem;
}

.pohodaSet__table th,
.pohodaSet__table td {
  vertical-align: middle;
}

.pohodaSet__table .gf-input {
  width: 100%;
}

.pohodaSet__notice {
  margin: 0;
  font-size: var(--gf-text-sm);
}

.pohodaSet__notice--bad {
  color: var(--gf-color-danger);
}

.pohodaSet__actions {
  display: flex;
  justify-content: flex-end;
}
</style>
